import React, { memo, useCallback } from "react";
import PropTypes from "prop-types";
import { Handle, Position } from "reactflow";
import styled from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useWorkflowBuilder } from "src/contexts/workflowBuilderContext";

// Category color mapping
const CATEGORY_COLORS = {
  extract: {
    bg: "#E8F5E9",
    border: "#4CAF50",
    icon: "#2E7D32",
  },
  transform: {
    bg: "#E3F2FD",
    border: "#2196F3",
    icon: "#1565C0",
  },
  load: {
    bg: "#FFF3E0",
    border: "#FF9800",
    icon: "#E65100",
  },
  control: {
    bg: "#F3E5F5",
    border: "#9C27B0",
    icon: "#6A1B9A",
  },
  other: {
    bg: "#ECEFF1",
    border: "#607D8B",
    icon: "#37474F",
  },
};

// Category icon mapping
const CATEGORY_ICONS = {
  extract: "download",
  transform: "exchange-alt",
  load: "upload",
  control: "sliders-h",
  other: "cog",
};

// Component type specific icons
const COMPONENT_ICONS = {
  // Extract
  S3_CSV: "file-csv",
  S3_JSON: "file-code",
  GRAPHQL_QUERY: "project-diagram",
  API_REQUEST: "globe",
  LOCAL_FILE_CSV: "file-csv",
  LOCAL_FILE_JSON: "file-code",

  // Transform
  FIELD_MAPPER: "random",
  DATA_FILTER: "filter",
  DATA_TRANSFORMER: "magic",
  AGGREGATE: "layer-group",
  JOIN: "code-branch",
  CUSTOM_JS: "code",

  // Load
  DB_UPSERT: "database",
  GRAPHQL_MUTATION: "project-diagram",
  NOTIFICATION: "bell",
  WEBHOOK: "paper-plane",

  // Control
  CONDITIONAL_BRANCH: "code-branch",
  LOOP: "sync",
  APPROVAL_GATE: "user-check",
  DELAY: "clock",
  ERROR_HANDLER: "exclamation-triangle",
};

const NodeContainer = styled.div`
  background: ${(props) => props.$colors?.bg || CATEGORY_COLORS.other.bg};
  border: 2px solid
    ${(props) => props.$colors?.border || CATEGORY_COLORS.other.border};
  border-radius: ${themeGet("radii.2")};
  padding: 0;
  min-width: 180px;
  max-width: 240px;
  box-shadow: ${(props) =>
    props.$isSelected
      ? `0 0 0 2px ${
          props.$colors?.border || CATEGORY_COLORS.other.border
        }, 0 4px 12px rgba(0,0,0,0.15)`
      : "0 2px 8px rgba(0,0,0,0.1)"};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }
`;

const NodeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: ${(props) =>
    props.$colors?.border || CATEGORY_COLORS.other.border}20;
  border-bottom: 1px solid
    ${(props) => props.$colors?.border || CATEGORY_COLORS.other.border}40;
  border-radius: ${themeGet("radii.2")} ${themeGet("radii.2")} 0 0;
`;

const IconWrapper = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${(props) => props.$colors?.icon || CATEGORY_COLORS.other.icon};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
`;

const NodeTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: #1a1a1a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

const NodeBody = styled.div`
  padding: 10px 12px;
`;

const NodeType = styled.div`
  font-size: 11px;
  color: ${(props) => props.$colors?.icon || "#666"};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
`;

const StepKey = styled.div`
  font-size: 10px;
  color: #999;
  margin-top: 4px;
  font-family: monospace;
`;

const StatusBadge = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${(props) => {
    switch (props.$status) {
      case "running":
        return "#2196F3";
      case "completed":
        return "#4CAF50";
      case "failed":
        return "#F44336";
      case "paused":
        return "#FF9800";
      default:
        return "transparent";
    }
  }};
  display: ${(props) => (props.$status ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const handleStyle = {
  width: 12,
  height: 12,
  border: "2px solid #fff",
  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
};

function WorkflowStepNode({ id, data, selected }) {
  const { selectNode } = useWorkflowBuilder();

  const category = data.category || "other";
  const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS.other;
  const categoryIcon = CATEGORY_ICONS[category] || "cog";
  const componentIcon = COMPONENT_ICONS[data.componentType] || categoryIcon;

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      selectNode(id);
    },
    [id, selectNode]
  );

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        style={{ ...handleStyle, background: colors.border }}
      />

      <NodeContainer
        $colors={colors}
        $isSelected={selected}
        onClick={handleClick}
      >
        <StatusBadge $status={data.runStatus}>
          {data.runStatus === "running" && (
            <FontAwesomeIcon icon={["fas", "spinner"]} spin />
          )}
          {data.runStatus === "completed" && (
            <FontAwesomeIcon icon={["fas", "check"]} />
          )}
          {data.runStatus === "failed" && (
            <FontAwesomeIcon icon={["fas", "times"]} />
          )}
          {data.runStatus === "paused" && (
            <FontAwesomeIcon icon={["fas", "pause"]} />
          )}
        </StatusBadge>

        <NodeHeader $colors={colors}>
          <IconWrapper $colors={colors}>
            <FontAwesomeIcon icon={["fas", componentIcon]} />
          </IconWrapper>
          <NodeTitle title={data.name || data.label}>
            {data.name || data.label}
          </NodeTitle>
        </NodeHeader>

        <NodeBody>
          <NodeType $colors={colors}>
            {data.componentType?.replace(/_/g, " ")}
          </NodeType>
          <StepKey>{data.stepKey}</StepKey>
        </NodeBody>
      </NodeContainer>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{ ...handleStyle, background: colors.border }}
      />
    </>
  );
}

WorkflowStepNode.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.shape({
    name: PropTypes.string,
    label: PropTypes.string,
    stepKey: PropTypes.string,
    componentType: PropTypes.string,
    category: PropTypes.string,
    runStatus: PropTypes.string,
  }).isRequired,
  selected: PropTypes.bool,
};

export default memo(WorkflowStepNode);
